import { isSameWorkshopAssetId } from './workshopAssetState.js';

// Match the existing /api/upload/asset multipart limit; do not impose model-specific limits here.
const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;
const IMAGE_EXTENSION = /\.(?:png|jpe?g|webp|gif|avif|bmp|heic|heif|tiff?|svg)$/i;

/** Serialize writes for one parent without blocking other workshop assets or retrying HTTP. */
export function createWorkshopAssetSaveQueue() {
  const pending = new Map();
  return {
    run(assetId, work) {
      const key = String(assetId ?? 'new');
      const next = (pending.get(key) || Promise.resolve()).catch(() => undefined).then(work);
      pending.set(key, next);
      const release = () => { if (pending.get(key) === next) pending.delete(key); };
      void next.then(release, release);
      return next;
    },
  };
}

/** Late library status/create results may only update the matching image's current-bank fields. */
export function mergeWorkshopAssetRegistration(latest, snapshot, fields) {
  if (!latest || !fields?.idField || !fields?.statusField) return latest;
  const mergeTarget = (current, incoming) => {
    if (!current?.imageUrl || current.imageUrl !== incoming?.imageUrl) return current;
    const next = { ...current };
    for (const field of [fields.idField, fields.statusField]) {
      if (Object.hasOwn(incoming, field)) next[field] = incoming[field];
    }
    return next;
  };
  const merged = mergeTarget(latest, snapshot);
  if (!Array.isArray(latest.variations)) return merged;
  return {
    ...merged,
    variations: latest.variations.map((variant, index) => mergeTarget(variant, snapshot?.variations?.[index])),
  };
}

export function validateVariantImageFile(file) {
  if (!file) throw new Error('请选择变体图片');
  const type = String(file.type || '').toLowerCase();
  if (!(type.startsWith('image/') || (!type && IMAGE_EXTENSION.test(file.name || '')))) {
    throw new Error('请选择图片文件');
  }
  if (!Number.isFinite(file.size) || file.size <= 0) throw new Error('图片文件为空');
  if (file.size >= MAX_UPLOAD_BYTES) throw new Error('图片过大，请上传小于 100MB 的图片');
}

export function variantTitleFromFile(file) {
  return String(file?.name || '').replace(/^.*[\\/]/, '').replace(IMAGE_EXTENSION, '').trim() || '上传变体';
}

export function appendUploadedWorkshopVariant(asset, { title, imageUrl, description = '' }) {
  if (!String(title || '').trim()) throw new Error('请填写变体名称');
  if (typeof imageUrl !== 'string' || !/^(?:https?:\/\/|\/(?!\/))/.test(imageUrl.trim())) {
    throw new Error('图片上传结果无效，请重新上传');
  }
  if (asset?.variations != null && !Array.isArray(asset.variations)) {
    throw new Error('变体列表读取失败，请刷新后重试');
  }
  return {
    ...asset,
    variations: [...(asset.variations || []), {
      title: String(title).trim(),
      imageUrl: imageUrl.trim(),
      description: String(description || '').trim(),
    }],
  };
}

function requireCurrentAsset(getAsset, assetId, projectId) {
  const asset = getAsset(assetId);
  if (!asset || !isSameWorkshopAssetId(asset.id, assetId)) {
    throw new Error('原资产已删除或已切换项目，请返回原资产后重试');
  }
  if (asset.projectId != null && !isSameWorkshopAssetId(asset.projectId, projectId)) {
    throw new Error('资产所属项目已变化，未保存变体');
  }
  return asset;
}

/** Upload a final image, then append to the latest parent. Never invokes AI or vendor ingestion. */
export async function uploadWorkshopVariant({
  assetId, projectId, file, title, description, uploadFile, getAsset, saveAsset, onBeforeSave,
}) {
  validateVariantImageFile(file);
  if (!String(title || '').trim()) throw new Error('请填写变体名称');
  if (assetId == null || projectId == null) throw new Error('缺少资产或项目，请刷新后重试');
  requireCurrentAsset(getAsset, assetId, projectId);
  const imageUrl = await uploadFile(file);
  // The user may have edited/added variants while the file was uploading.
  // Do not resurrect a deleted parent or overwrite its list using the starting snapshot.
  const rebase = () => {
    const latest = requireCurrentAsset(getAsset, assetId, projectId);
    return { ...appendUploadedWorkshopVariant(latest, { title, imageUrl, description }), projectId: latest.projectId ?? projectId };
  };
  const updated = rebase();
  onBeforeSave?.(assetId);
  // The parent can have another POST already in flight. Re-read it inside its save queue too.
  const saved = await saveAsset(updated, rebase);
  const persistedVariant = saved?.variations?.at(-1);
  if (!saved || !isSameWorkshopAssetId(saved.id, assetId)
      || persistedVariant?.imageUrl !== imageUrl.trim() || persistedVariant?.title !== String(title).trim()) {
    throw new Error('变体未保存，请返回原资产后重试');
  }
  return saved;
}
